import { IStateDataSpeciesList } from '../../../../FindUSSpecies/helpers/buildStateDataSpecies';
import {
  MAX_OPACITY,
  MIN_OPACITY,
  OPACITY_MULTIPLIER,
  STATE_BASE_COLOR_RGB,
} from '../constants/constants';

interface CalculateStateColorsOptions {
  stateData: IStateDataSpeciesList[];
  speciesGroupingExternalId?: string;
}

interface StateWithCatches {
  regionCode: string;
  totalCatches: number;
}

/**
 * Calculates state colors based on relative percentage of totalCatches for species groupings.
 * Uses a single base color with varying opacity based on each state's percentage of total catches.
 * Returns a mapping of regionCode to rgba color that can be used with CountryMap.
 *
 * Supports two calculation modes:
 * 1. Aggregate ALL: When no speciesGroupingExternalId is provided, sums all species groupings
 * 2. Filter by ID: When speciesGroupingExternalId is provided, shows data for that specific grouping
 * @example
 * // Example 1: Show total catches for ALL species groupings per state
 * ```tsx
 * const stateColors = calculateStateColors({
 *   stateData: topSpeciesByState,
 * });
 * // Returns: { 'AL': 'rgba(0, 102, 148, 0.85)', 'CA': 'rgba(0, 102, 148, 0.42)', ... }
 * ```
 * @example
 * // Example 2: Show catches for a specific species grouping (e.g., Bass)
 * ```tsx
 * const stateColors = calculateStateColors({
 *   stateData: topSpeciesByState,
 *   speciesGroupingExternalId: 'seo_bass',
 * });
 * // Returns: { 'AL': 'rgba(0, 102, 148, 0.92)', 'TX': 'rgba(0, 102, 148, 0.67)', ... }
 * ```
 */
export function calculateStateColors({
  stateData,
  speciesGroupingExternalId,
}: CalculateStateColorsOptions): Record<string, string> {
  const isFiltering = !!speciesGroupingExternalId;

  // First pass: collect all state catch data and calculate total
  const statesWithCatches: StateWithCatches[] = [];
  let totalCatchesAcrossAllStates = 0;

  stateData.forEach(state => {
    const dataArray = state.speciesGroupings;

    let stateTotalCatches = 0;

    if (isFiltering) {
      // Mode A: Filter by specific species grouping ID
      const targetItem = dataArray.find(item => item.externalId === speciesGroupingExternalId);
      if (targetItem) {
        stateTotalCatches = parseInt(targetItem.totalCatches, 10);
      }
    } else {
      // Mode B: Aggregate ALL species groupings
      stateTotalCatches = dataArray.reduce((sum, item) => {
        const catches = parseInt(item.totalCatches, 10);
        return sum + (isNaN(catches) ? 0 : catches);
      }, 0);
    }

    // Add to results if we have valid catches
    if (!isNaN(stateTotalCatches) && stateTotalCatches > 0) {
      const regionCode = state.stateId;
      statesWithCatches.push({ regionCode, totalCatches: stateTotalCatches });
      totalCatchesAcrossAllStates += stateTotalCatches;
    }
  });

  // If no catches found, return empty object
  if (totalCatchesAcrossAllStates === 0) {
    return {};
  }

  // Second pass: calculate percentage and map to opacity
  const stateColors: Record<string, string> = {};

  statesWithCatches.forEach(({ regionCode, totalCatches }) => {
    // Calculate percentage of total catches
    const percentage = totalCatches / totalCatchesAcrossAllStates;

    // Map percentage to opacity range (MIN_OPACITY to MAX_OPACITY)
    // We use a linear mapping: opacity = MIN_OPACITY + (percentage * opacity_range)
    // But cap it at MAX_OPACITY to avoid overflow
    const opacityRange = MAX_OPACITY - MIN_OPACITY;
    const opacity = Math.min(
      MIN_OPACITY + percentage * opacityRange * OPACITY_MULTIPLIER,
      MAX_OPACITY,
    );

    // Round to 2 decimal places for cleaner output
    const roundedOpacity = Math.round(opacity * 100) / 100;

    // Create rgba color string
    stateColors[regionCode] =
      `rgba(${STATE_BASE_COLOR_RGB.r}, ${STATE_BASE_COLOR_RGB.g}, ${STATE_BASE_COLOR_RGB.b}, ${roundedOpacity})`;
  });

  return stateColors;
}
